/**
 * Shopify Storefront API client
 * Uses the public Storefront Access Token (safe for browser use).
 * Docs: https://shopify.dev/docs/api/storefront
 */

const DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || "2024-10";
const PRODUCT_TITLE = process.env.REACT_APP_SHOPIFY_PRODUCT_TITLE || "";

const ENDPOINT = `https://${DOMAIN}/api/${API_VERSION}/graphql.json`;

async function shopifyFetch(query, variables = {}) {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": TOKEN,
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length) {
    // Filter out access-scope-only errors so partial data still resolves.
    const fatal = json.errors.filter(
      (e) => !(e.extensions && e.extensions.code === "ACCESS_DENIED")
    );
    if (fatal.length) {
      throw new Error(fatal.map((e) => e.message).join("; "));
    }
  }
  return json.data;
}

/* ------------------------------------------------------------- */
/*  Product fetch by title (exact match preferred, contains fallback) */
/* ------------------------------------------------------------- */
const PRODUCT_QUERY = `
  query ProductByTitle($query: String!) {
    products(first: 5, query: $query) {
      edges {
        node {
          id
          title
          handle
          description
          availableForSale
          totalInventory
          featuredImage { url altText width height }
          images(first: 6) {
            edges { node { url altText width height } }
          }
          priceRange {
            minVariantPrice { amount currencyCode }
          }
          compareAtPriceRange {
            minVariantPrice { amount currencyCode }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                availableForSale
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
              }
            }
          }
        }
      }
    }
  }
`;

export async function fetchProductByTitle(title = PRODUCT_TITLE) {
  if (!DOMAIN || !TOKEN) {
    throw new Error("Shopify credentials are not configured.");
  }
  if (!title) throw new Error("No product title provided.");

  // Storefront search: try exact title, then wildcard fallback
  const queries = [`title:"${title}"`, `title:*${title.split("|")[0].trim()}*`];

  for (const q of queries) {
    const data = await shopifyFetch(PRODUCT_QUERY, { query: q });
    const edges = (data && data.products && data.products.edges) || [];
    // Exact-match first, else first result
    const exact = edges.find((e) => e.node.title === title);
    const node = (exact && exact.node) || (edges[0] && edges[0].node);
    if (node) return normalizeProduct(node);
  }
  throw new Error(`Product not found on Shopify: "${title}"`);
}

function normalizeProduct(p) {
  const variants = p.variants.edges.map((e) => e.node);
  const images = p.images.edges.map((e) => e.node);
  const firstVariant = variants[0] || null;
  const price = firstVariant ? firstVariant.price : p.priceRange.minVariantPrice;
  const compareAt =
    (firstVariant && firstVariant.compareAtPrice) ||
    (p.compareAtPriceRange &&
      p.compareAtPriceRange.minVariantPrice &&
      Number(p.compareAtPriceRange.minVariantPrice.amount) > 0
      ? p.compareAtPriceRange.minVariantPrice
      : null);
  return {
    id: p.id,
    title: p.title,
    handle: p.handle,
    description: p.description,
    availableForSale: p.availableForSale,
    totalInventory: p.totalInventory,
    featuredImage: p.featuredImage,
    images,
    variants,
    firstVariant,
    price,
    compareAtPrice: compareAt,
    currency: price ? price.currencyCode : "INR",
  };
}

/* ------------------------------------------------------------- */
/*  Cart persistence — shared across products via localStorage    */
/* ------------------------------------------------------------- */
const CART_STORAGE_KEY = "rudrima:shopify:cartId";

export const getStoredCartId = () => {
  try { return typeof window !== "undefined" ? window.localStorage.getItem(CART_STORAGE_KEY) : null; }
  catch (_) { return null; }
};
export const setStoredCartId = (id) => {
  try { if (typeof window !== "undefined") window.localStorage.setItem(CART_STORAGE_KEY, id); } catch (_) {}
};
export const clearStoredCartId = () => {
  try { if (typeof window !== "undefined") window.localStorage.removeItem(CART_STORAGE_KEY); } catch (_) {}
};

/* ------------------------------------------------------------- */
/*  Cart create + checkout redirect                              */
/* ------------------------------------------------------------- */
const CART_CREATE_MUTATION = `
  mutation CartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
      }
      userErrors { field message code }
    }
  }
`;

export async function createCart(variantId, quantity = 1) {
  if (!variantId) throw new Error("Missing variantId");
  const data = await shopifyFetch(CART_CREATE_MUTATION, {
    input: {
      lines: [{ quantity, merchandiseId: variantId }],
    },
  });
  const { cart, userErrors } = data.cartCreate;
  if (userErrors && userErrors.length) {
    throw new Error(userErrors.map((e) => e.message).join("; "));
  }
  if (cart && cart.id) setStoredCartId(cart.id);
  return cart; // { id, checkoutUrl, totalQuantity }
}

/* Optional: add lines to an existing cart */
const CART_LINES_ADD_MUTATION = `
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { id checkoutUrl totalQuantity }
      userErrors { field message code }
    }
  }
`;

export async function addToCart(cartId, variantId, quantity = 1) {
  const data = await shopifyFetch(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity, merchandiseId: variantId }],
  });
  const { cart, userErrors } = data.cartLinesAdd;
  if (userErrors && userErrors.length) {
    throw new Error(userErrors.map((e) => e.message).join("; "));
  }
  return cart;
}

/* Fetch existing cart (to validate stored cartId is still valid) */
const CART_QUERY = `
  query Cart($cartId: ID!) {
    cart(id: $cartId) { id checkoutUrl totalQuantity }
  }
`;

export async function getCart(cartId) {
  const data = await shopifyFetch(CART_QUERY, { cartId });
  return data && data.cart ? data.cart : null;
}

/* Cart change broadcast (header badge / drawer) */
export const CART_EVENT = "rudrima:cart-updated";
export const notifyCart = () => { try { window.dispatchEvent(new Event(CART_EVENT)); } catch (_) {} };

const CART_DETAIL_QUERY = `
  query CartDetail($cartId: ID!) {
    cart(id: $cartId) {
      id checkoutUrl totalQuantity
      discountCodes { code applicable }
      cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
      discountAllocations { discountedAmount { amount currencyCode } }
      lines(first: 20) {
        edges { node {
          id quantity
          cost { totalAmount { amount currencyCode } }
          discountAllocations { discountedAmount { amount currencyCode } }
          merchandise { ... on ProductVariant {
            id title
            price { amount currencyCode }
            product { title handle featuredImage { url altText } }
          } }
        } }
      }
    }
  }
`;

export async function getCartDetails() {
  const id = getStoredCartId();
  if (!id) return null;
  try {
    const data = await shopifyFetch(CART_DETAIL_QUERY, { cartId: id });
    const c = data && data.cart;
    if (!c) { clearStoredCartId(); return null; }
    const lines = c.lines.edges.map((e) => e.node);
    const saving = sumSaving(c);
    const codes = (c.discountCodes || []).filter((d) => d.applicable).map((d) => d.code);
    return { ...c, lines, saving, codes };
  } catch (_) {
    return null;
  }
}

const CART_LINES_UPDATE = `
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { id totalQuantity } userErrors { message } }
  }
`;
const CART_LINES_REMOVE = `
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { id totalQuantity } userErrors { message } }
  }
`;

export async function updateCartLine(lineId, quantity) {
  const cartId = getStoredCartId();
  const data = await shopifyFetch(CART_LINES_UPDATE, { cartId, lines: [{ id: lineId, quantity }] });
  const errs = data.cartLinesUpdate.userErrors;
  if (errs && errs.length) throw new Error(errs.map((e) => e.message).join("; "));
  notifyCart();
}

export async function removeCartLine(lineId) {
  const cartId = getStoredCartId();
  const data = await shopifyFetch(CART_LINES_REMOVE, { cartId, lineIds: [lineId] });
  const errs = data.cartLinesRemove.userErrors;
  if (errs && errs.length) throw new Error(errs.map((e) => e.message).join("; "));
  notifyCart();
}

/**
 * Adds a variant to the shared cart. Reuses cartId from localStorage
 * when possible; falls back to creating a new cart. Returns the up-to-date cart.
 * If the stored cart id is stale/invalid, silently creates a fresh cart.
 */
export async function addLineToSharedCart(variantId, quantity = 1) {
  const existingId = getStoredCartId();
  if (existingId) {
    try {
      const cart = await addToCart(existingId, variantId, quantity);
      if (cart && cart.id) {
        setStoredCartId(cart.id);
        notifyCart();
        return cart;
      }
    } catch (_) {
      clearStoredCartId(); // fall through to create fresh cart
    }
  }
  const cart = await createCart(variantId, quantity);
  notifyCart();
  return cart;
}

/* ------------------------------------------------------------- */
/*  Bundle / discount code helpers                                */
/* ------------------------------------------------------------- */
const CART_COST_FIELDS = `
  id checkoutUrl totalQuantity
  discountCodes { code applicable }
  cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
  discountAllocations { discountedAmount { amount currencyCode } }
  lines(first: 20) { edges { node { discountAllocations { discountedAmount { amount currencyCode } } } } }
`;

const sumSaving = (cart) => {
  if (!cart) return 0;
  const cartLevel = (cart.discountAllocations || []).reduce((s, d) => s + Number(d.discountedAmount.amount), 0);
  const lineLevel = ((cart.lines && cart.lines.edges) || []).reduce(
    (s, e) => s + (e.node.discountAllocations || []).reduce((t, d) => t + Number(d.discountedAmount.amount), 0), 0);
  return cartLevel + lineLevel;
};

const BUNDLE_PREVIEW_MUTATION = `
  mutation BundlePreview($input: CartInput!) {
    cartCreate(input: $input) { cart { ${CART_COST_FIELDS} } userErrors { message } }
  }
`;

/* Creates a throw-away cart to read Shopify's real discounted price (not stored). */
export async function previewBundle(variantIds, code) {
  const data = await shopifyFetch(BUNDLE_PREVIEW_MUTATION, {
    input: { lines: variantIds.map((id) => ({ quantity: 1, merchandiseId: id })), discountCodes: code ? [code] : [] },
  });
  const cart = data.cartCreate.cart;
  if (!cart) return null;
  const dc = (cart.discountCodes || []).find((d) => d.code.toLowerCase() === (code || "").toLowerCase());
  return {
    subtotal: Number(cart.cost.subtotalAmount.amount),
    total: Number(cart.cost.totalAmount.amount),
    currency: cart.cost.subtotalAmount.currencyCode,
    saving: sumSaving(cart),
    codeApplicable: !!(dc && dc.applicable),
  };
}

const CART_DISCOUNT_UPDATE = `
  mutation CartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart { ${CART_COST_FIELDS} } userErrors { message }
    }
  }
`;

export async function applyDiscountCode(code) {
  const cartId = getStoredCartId();
  if (!cartId || !code) return null;
  const data = await shopifyFetch(CART_DISCOUNT_UPDATE, { cartId, discountCodes: [code] });
  const cart = data.cartDiscountCodesUpdate.cart;
  notifyCart();
  return cart ? { ...cart, saving: sumSaving(cart) } : null;
}

/* Currency helper */
export const money = (amount, currency = "INR") => {
  const num = Number(amount);
  if (Number.isNaN(num)) return "";
  const symbol = currency === "INR" ? "₹" : currency + " ";
  return `${symbol}${num.toLocaleString("en-IN", {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export const SHOPIFY_PRODUCT_TITLE = PRODUCT_TITLE;
